/**
 * Benefits in Kind (BIK) Management
 * 
 * Main page for managing BIK for IR8A Appendix 8A reporting.
 * Access: HR Admin only
 * 
 * Tabs:
 * - Employee BIK: Per-employee consolidated breakdown & calculation
 * - Accommodation: Dormitory BIK (taxable for all residents, no pass-type exemption)
 * - Motor Vehicles: Vehicle BIK assignments
 * - One-Time Benefits: Company trips, long service awards
 * - IR8A Export: Verification workflow and export
 * 
 * Design System: Admin Theme (Red)
 */

import React, { useState } from 'react';
import {
  Gift,
  Home,
  Car,
  Download,
  Users,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTabPersistence } from '../../../hooks/usePersistence';

// Tab components
import EmployeeBIKTab from './components/EmployeeBIKTab';
import AccommodationBIKTab from './components/AccommodationBIKTab';
import MotorVehicleBIKTab from './components/MotorVehicleBIKTab';
import OneTimeBenefitsTab from './components/OneTimeBenefitsTab';
import IR8AExportTab from './components/IR8AExportTab';

const BIKManagement = () => {
  const { user } = useAuth();
  
  // Tab persistence
  const validTabs = ['employee', 'accommodation', 'motor-vehicles', 'one-time', 'ir8a-export'];
  const [activeTab, setActiveTab] = useTabPersistence('bik-management', 'employee', validTabs);
  
  // Year selection (default to current year)
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  
  // Generate year options (current year and 5 years back)
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    yearOptions.push(y);
  }
  
  // Tab definitions
  const tabs = [
    {
      id: 'employee',
      label: 'Employee BIK',
      icon: Users,
      description: 'Per-employee BIK breakdown & calculation',
    },
    {
      id: 'accommodation',
      label: 'Accommodation',
      icon: Home,
      description: 'Dormitory BIK (d2)',
    },
    {
      id: 'motor-vehicles',
      label: 'Motor Vehicles',
      icon: Car,
      description: 'Vehicle BIK (d5/d6)',
    },
    {
      id: 'one-time',
      label: 'One-Time Benefits',
      icon: Gift,
      description: 'Trips, awards (d4)',
    },
    {
      id: 'ir8a-export',
      label: 'IR8A Export',
      icon: Download,
      description: 'Appendix 8A export',
    },
  ];
  
  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'employee':
        return <EmployeeBIKTab year={selectedYear} />;
      case 'accommodation':
        return <AccommodationBIKTab year={selectedYear} />;
      case 'motor-vehicles':
        return <MotorVehicleBIKTab year={selectedYear} />;
      case 'one-time':
        return <OneTimeBenefitsTab year={selectedYear} />;
      case 'ir8a-export':
        return <IR8AExportTab year={selectedYear} />;
      default:
        return <EmployeeBIKTab year={selectedYear} />;
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border">
        <div className="px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 mb-1 min-w-0">
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                </div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">
                  Benefits in Kind Management
                </h1>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">
                Manage BIK for IRAS IR8A Appendix 8A reporting
              </p>
            </div>
            
            {/* Year Selector */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <label className="text-sm font-medium text-gray-700">
                Tax Year:
              </label>
              <div className="relative">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="select-admin pr-10"
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year} (YOA {year + 1})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tab Navigation - Pill Button Cluster */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
              title={tab.description}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>
      
      {/* Tab Content */}
      <div>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default BIKManagement;
